/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isTesting } from '@embroider/macros';
import Component from '@glimmer/component';
import type NavigationService from 'parog-web/services/navigation';
import type SemeiaSessionService from 'parog-web/services/session';
import runtimeEnvironment from 'parog-web/utils/runtime-environment';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import can from 'ember-can/helpers/can';
import Button from 'ember-wiser/components/button';
import PatientSearch from 'parog-web/components/layout/medics-web/patient-search';
import ProfileMenu from 'parog-web/components/layout/medics-web/profile-menu';
import type AnalyticsService from 'parog-web/services/analytics';
import ParogLogo from 'ui-kit/components/parog-logo';
// tells Tailwind that h-[50px] must be part of the generated CSS
const navbarHeightStyle = 'h-[50px]';
export function navbarHeight() {
  return (navbarHeightStyle.match(/\d+/) as [string])[0];
}

const firstLevelPages = [
  'medics-web.protected.index.alerts-and-notifications',
  'medics-web.protected.index.alerts-and-notifications_loading',
  'medics-web.protected.index.tasks-and-consultations',
  'medics-web.protected.index.tasks-and-consultations_loading',
  'medics-web.protected.index.patient-list',
  'medics-web.protected.index.patient-list_loading',
  'patient-web.protected_loading',
  'patient-web.protected.dashboard.index'
];

const secondLevelPages = [
  'medics-web.protected.patients.patient.dashboard.main',
  'medics-web.protected.patients.patient.dashboard.main.index',
  'medics-web.protected.patients.patient.dashboard.bio-results.summary',
  'medics-web.protected.medics',
  'patient-web.protected.blood-pressure.sessions.index'
];

const returnToMedicsWebDashboardPages = [
  'medics-web.protected.patients.patient.feature.component.index',
  'medics-web.protected.patients.patient.feature.component.edition',
  'medics-web.protected.patients.patient.form',
  'medics-web.protected.patients.patient.administrative.index'
];

interface Signature {
  Args: { shouldHideProfileIcon?: boolean };
  Blocks: { default: [] };
}

export default class NavBar extends Component<Signature> {
  @service declare analytics: AnalyticsService;
  @service declare navigation: NavigationService;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  menuStyles = `ui top menu no-print fixed !min-h-[0px] bg-pink [box-shadow:none] ${navbarHeightStyle}`;
  navbarHeightStyle = navbarHeightStyle;
  titleItemSytles =
    'grow-[2] text-center !font-bold [@media_screen_and_(max-width:549px)]:m-0';

  get _isTestMode() {
    return isTesting();
  }

  get isOnFirstLevelPage() {
    // Is there a better way of determining what route we end up on
    // after transitioning to 'index'?
    return firstLevelPages.includes(this.navigation.currentRouteName);
  }

  get logoColor(): string {
    const env = runtimeEnvironment();
    switch (env) {
      case 'production':
      case 'local':
      case 'test':
        return '#0d6efd';
      case 'preproduction':
        return '#510C7B';
      default:
        return '#53B1A5';
    }
  }

  get rootRoute() {
    if (this.session.medic) {
      if (this.isOnFirstLevelPage)
        return this.router.currentRouteName || 'index';
      return 'medics-web.protected.index';
    } else if (this.session.patient) {
      return 'patient-web.protected.dashboard.index';
    }

    return 'index';
  }

  get shouldReturnToMedicsWebDashboard() {
    return returnToMedicsWebDashboardPages.includes(
      this.navigation.currentRouteName
    );
  }

  get shouldShowBack() {
    if (this.#isOnSecondLevelPage) return false;
    return this.navigation.canGoBack;
  }

  get #isOnSecondLevelPage() {
    return secondLevelPages.includes(this.navigation.currentRouteName);
  }

  back = () => {
    if (this.shouldReturnToMedicsWebDashboard) {
      this.router.transitionTo(
        'medics-web.protected.patients.patient.dashboard'
      );
    } else {
      this.navigation.back();
    }
  };

  onCreatePatientClick = () => {
    this.analytics.trackAction([
      'Bouton Cliqué',
      {
        button_name: 'Header - Créer patient'
      }
    ]);
    this.router.transitionTo('medics-web.protected.patients.new');
  };

  <template>
    <nav class={{this.menuStyles}}>
      <div class='left menu'>
        <div class='item' data-test-logo-wrapper-item>

          <LinkTo
            @route={{this.rootRoute}}
            @activeClass=''
            data-test-logo-route
          >
            <ParogLogo @style='small' @color={{this.logoColor}} />
          </LinkTo>
        </div>
        {{#unless this.isOnFirstLevelPage}}
          {{#if this.shouldShowBack}}
            <div class='item'>
              <button
                type='button'
                class='ui tertiary button text-[1.1rem] [&.ui.tertiary.button]:tracking-[0] [&.ui.tertiary.button]:text-white [&_.icon.chevron.left]:mt-[-0.12em] [.ui.menu:not(.vertical)_.item>&.button]:text-[1.1rem]'
                data-test-back-link
                {{on 'click' this.back}}
              >
                <i class='chevron left icon'></i>
                <span class='hiding on mobile'>
                  {{if
                    this.shouldReturnToMedicsWebDashboard
                    'Fiche patient'
                    'Retour'
                  }}
                </span>
              </button>
            </div>
          {{else}}

            <LinkTo
              @route={{this.rootRoute}}
              class='item text-[1.1rem] [&_.icon.chevron.left]:mt-[-0.12em] [.ui.menu:not(.vertical)_.item>&.button]:text-[1.1rem]'
              data-test-index-link
            >
              <i class='chevron left icon'></i>
              <span class='hiding on mobile'>Accueil</span>
            </LinkTo>
          {{/if}}
        {{/unless}}

        {{#unless this.session.medic}}
          <div class='item {{this.titleItemSytles}}'>
            {{yield}}
          </div>
        {{/unless}}
      </div>

      {{#if this.session.medic}}
        <div
          class='ui container [&.ui.ui.ui.container:not(.fluid)]:grow [&.ui.ui.ui.container:not(.fluid)]:justify-between [&.ui.ui.ui.container:not(.fluid)]:px-[0.85em] [&.ui.ui.ui.container:not(.fluid)]:py-0'
        >
          <div
            class='item
              {{this.titleItemSytles}}
              text-center font-bold [&_div[data-test-actions-dropdown]]:p-0 [.ui.menu_&.item]:grow-[2]'
          >
            {{yield}}
          </div>
          <div class='flex gap-2'>
            {{#if (can 'create patient')}}
              <Button
                class='my-2'
                @variant='primary-white-border'
                @icon='Patient'
                @ariaLabel='CRÉER UN PATIENT'
                {{on 'click' this.onCreatePatientClick}}
              >
                Créer un patient
              </Button>
            {{/if}}

            {{! @glint-expect-error }}
            <PatientSearch class='item' />
          </div>
        </div>

        {{#unless @shouldHideProfileIcon}}
          <ProfileMenu
            @currentMedic={{this.session.medic}}
            {{! @glint-expect-error }}
            class='right menu'
          />
        {{/unless}}
      {{/if}}
    </nav>

    <div class='no-print {{this.navbarHeightStyle}}'></div>

    {{!
  Forces the bold font to load early. This will not slow down the
  initial loading of the website, it should only trigger a background
  download of the font by the browser.
}}
    <b> </b>

    {{!
  Hack for testing

  in test mode, the history API is not available because the NoneLocation is used
  https://github.com/emberjs/ember.js/blob/master/packages/@ember/-internals/routing/lib/location/none_location.ts

  But in some tests, we might want to exit the current route with something similar to the back link

  So let's add a hidden link to index that might be used only if necessary
}}
    {{#if this._isTestMode}}
      <div class='hidden'>
        <LinkTo @route='index' data-test-hidden-index-link>
          test mode only link to index
          <br />
          Use this only if not other link on the page allows you to exit the
          current route
        </LinkTo>
      </div>
    {{/if}}
  </template>
}
